import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { TrendingUp, Loader2, Send, X, RefreshCw, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OpsTabs from "@/components/coach/OpsTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Progress Updates — human-reviewed monthly parent updates.
 * Drafts are generated monthly (owner gets an email ping) or on demand.
 * Owner sees every family; a coach sees only families assigned to them.
 * Nothing goes to a parent until someone reads it and hits Send.
 */
export default function ProgressUpdates() {
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const [tab, setTab] = useState("draft"); // draft | sent | dismissed
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/progress-updates`, { withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load updates", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            const r = await axios.post(`${API}/coach/progress-updates/generate`, {}, { withCredentials: true });
            toast.success(r.data.created > 0 ? `${r.data.created} new draft${r.data.created === 1 ? "" : "s"} generated` : "Nothing new — every active athlete already has a draft this month");
            load();
        } catch (e) {
            toast.error("Couldn't generate", { description: e?.response?.data?.detail });
        } finally {
            setGenerating(false);
        }
    };

    const visible = rows.filter((r) => r.status === tab);
    const counts = {
        draft: rows.filter((r) => r.status === "draft").length,
        sent: rows.filter((r) => r.status === "sent").length,
        dismissed: rows.filter((r) => r.status === "dismissed").length,
    };

    return (
        <main data-testid="progress-updates-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pt-24 md:pt-8">
            <OpsTabs />
            <div className="flex items-center justify-between gap-4 mb-2">
                <h1 className="font-display text-3xl tracking-wider flex items-center gap-3">
                    <TrendingUp className="w-7 h-7 text-brand-red" /> PROGRESS UPDATES
                </h1>
                {isOwner && (
                    <button type="button" data-testid="progress-generate" onClick={generate} disabled={generating} className="btn-ghost-tactical text-xs">
                        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Generate now
                    </button>
                )}
            </div>
            <p className="text-sm text-zinc-400 mb-6">
                Drafted monthly from real attendance + max data. Review, edit, then send — nothing goes out on its own.
                {!isOwner && " You see the families assigned to you."}
            </p>

            <div className="flex gap-1 mb-5">
                {["draft", "sent", "dismissed"].map((t) => (
                    <button
                        key={t}
                        type="button"
                        data-testid={`progress-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={`px-3.5 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                            tab === t ? "border-brand-red text-white bg-brand-red/10" : "border-zinc-800 text-zinc-400 hover:text-white"
                        }`}
                    >
                        {t} {counts[t] > 0 && <span className="font-bold ml-1">{counts[t]}</span>}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>
            ) : visible.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 border border-dashed border-zinc-800 rounded">
                    {tab === "draft" ? "No drafts waiting. New ones generate automatically each month." : `Nothing ${tab} yet.`}
                </div>
            ) : (
                <div className="space-y-4">
                    {visible.map((u) => (
                        <UpdateCard key={u.update_id} u={u} onChanged={load} />
                    ))}
                </div>
            )}
        </main>
    );
}

function UpdateCard({ u, onChanged }) {
    const [body, setBody] = useState(u.body);
    const [busy, setBusy] = useState(false);
    const dirty = body !== u.body;
    const isDraft = u.status === "draft";

    const act = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            toast.success(okMsg);
            onChanged();
        } catch (e) {
            toast.error("Failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const saveEdit = () => act(
        () => axios.patch(`${API}/coach/progress-updates/${u.update_id}`, { body }, { withCredentials: true }),
        "Draft saved",
    );
    const send = async () => {
        if (dirty) {
            await axios.patch(`${API}/coach/progress-updates/${u.update_id}`, { body }, { withCredentials: true });
        }
        return act(
            () => axios.post(`${API}/coach/progress-updates/${u.update_id}/send`, {}, { withCredentials: true }),
            `Sent to ${u.parent_email}`,
        );
    };
    const dismiss = () => act(
        () => axios.post(`${API}/coach/progress-updates/${u.update_id}/dismiss`, {}, { withCredentials: true }),
        "Dismissed",
    );

    return (
        <article data-testid={`progress-card-${u.update_id}`} className="border border-zinc-800 rounded-lg bg-zinc-950 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <Link
                        to={`/coach/athletes/${u.athlete_id}`}
                        data-testid={`progress-athlete-${u.update_id}`}
                        className="font-display text-lg tracking-wider text-white hover:text-brand-red transition-colors"
                    >
                        {u.athlete_name || "Athlete"}
                    </Link>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        {u.period} · to {u.parent_email}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase">
                    <span className="border border-zinc-700 text-zinc-300 px-2 py-1">
                        {u.stats?.sessions_attended ?? 0} session{(u.stats?.sessions_attended ?? 0) === 1 ? "" : "s"}
                    </span>
                    {(u.stats?.prs || []).length > 0 && (
                        <span className="border border-emerald-600/50 text-emerald-300 px-2 py-1">
                            {u.stats.prs.length} PR{u.stats.prs.length === 1 ? "" : "s"}
                        </span>
                    )}
                    {u.status === "sent" && (
                        <span className="border border-emerald-600/50 text-emerald-300 px-2 py-1 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                    )}
                </div>
            </div>

            {isDraft ? (
                <>
                    <textarea
                        data-testid={`progress-body-${u.update_id}`}
                        className="w-full mt-3 bg-zinc-900 border border-zinc-700 rounded px-3 py-2.5 text-sm text-zinc-100 leading-relaxed min-h-44 focus:border-brand-red outline-none"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                    />
                    <div className="flex justify-end gap-2 mt-3">
                        <button type="button" data-testid={`progress-dismiss-${u.update_id}`} onClick={dismiss} disabled={busy} className="btn-ghost-tactical text-xs text-zinc-400">
                            <X className="w-3 h-3" /> Dismiss
                        </button>
                        {dirty && (
                            <button type="button" data-testid={`progress-save-${u.update_id}`} onClick={saveEdit} disabled={busy} className="btn-ghost-tactical text-xs">
                                Save draft
                            </button>
                        )}
                        <button type="button" data-testid={`progress-send-${u.update_id}`} onClick={send} disabled={busy} className="btn-tactical text-xs">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send to parent
                        </button>
                    </div>
                </>
            ) : (
                <pre className="mt-3 text-sm text-zinc-400 whitespace-pre-wrap font-sans leading-relaxed border-l-2 border-zinc-800 pl-3">{u.body}</pre>
            )}
        </article>
    );
}
